// Internal engine types for Corridor Zero. These are deliberately small and
// data-oriented (typed arrays for the map/framebuffer) so the render + sim loop
// stays allocation-free at 60fps.

/** A square, power-of-two RGBA texture stored as packed little-endian Uint32 pixels. */
export interface Texture {
  readonly size: number;
  readonly data: Uint32Array;
}

/**
 * A billboard sprite frame: packed RGBA pixels with a transparent background
 * (alpha 0). `anchorY` is the fraction of the height that sits on the floor
 * line (1 = feet at bottom). Baked art will fill these same structures later.
 */
export interface SpriteFrame {
  readonly width: number;
  readonly height: number;
  readonly data: Uint32Array;
  /** Horizontal anchor as a fraction of width (0.5 = centered). */
  readonly anchorX: number;
  /** Vertical anchor as a fraction of height (1 = bottom sits on the ground). */
  readonly anchorY: number;
}

/** Wall texture id per cell. 0 = empty/walkable. Values index the TextureProvider. */
export type Cell = number;

export interface EnemySpawn {
  readonly x: number;
  readonly y: number;
  readonly kind: string;
}

export interface Level {
  readonly width: number;
  readonly height: number;
  /** Row-major grid of wall texture ids; 0 is walkable space. */
  readonly cells: Uint8Array;
  readonly floorTexId: number;
  readonly ceilTexId: number;
  readonly player: { x: number; y: number; angle: number };
  readonly enemies: readonly EnemySpawn[];
  /** Optional per-cell light multiplier (0..1); undefined = uniform lighting. */
  readonly light?: Float32Array;
}

/** Supplies wall/floor/ceiling textures by id. Runtime-procedural now; baked PNG atlas later. */
export interface TextureProvider {
  get(id: number): Texture;
}

export function cellAt(level: Level, cx: number, cy: number): Cell {
  if (cx < 0 || cy < 0 || cx >= level.width || cy >= level.height) return 1;
  return level.cells[cy * level.width + cx] ?? 1;
}

export function isSolid(level: Level, cx: number, cy: number): boolean {
  return cellAt(level, cx, cy) !== 0;
}
